import { Injectable, inject } from '@angular/core';
import { filter, firstValueFrom } from 'rxjs';

import { ConfigurationService } from '@mobi/rwc-utils-common-ng-jslib';
import { MTC_ACA_ENDPOINT } from '@mobi/rwc-utils-observability-ng-jslib';

import { ACCESS_TOKEN_PROVIDER } from '../auth/access-token.provider';
import { AUTH_INFO_PROVIDER } from '../auth/auth-provider';
import { AUTHORIZATION_HEADER } from '../auth/internal/access-token-error-handler.service';

@Injectable({ providedIn: 'root' })
export class MtcExporterUtilsService {
    #accessToken = inject(ACCESS_TOKEN_PROVIDER, { optional: true })?.accessTokenOnce$.pipe(filter(Boolean));
    #configService = inject(ConfigurationService);
    #isCip = inject(AUTH_INFO_PROVIDER) === 'CIP';

    headers: () => Promise<Record<string, string>> = async () => {
        const headers: Record<string, string> = {
            tracestate: 'tk-id=' + this.#configService.getTkNameId(),
            traceparent: generateTraceparent()
        };

        if (this.#needsAccessToken() && this.#accessToken) {
            const auth = await firstValueFrom(this.#accessToken);
            headers[AUTHORIZATION_HEADER] = `Bearer ${auth}`;
        }

        return headers;
    };

    /**
     * only b2e (and b2eadmin) needs the auth headers
     * for CIP, b2b and b2c the headers are set in the gateway to the anonymous user
     */
    #needsAccessToken(): boolean {
        const infraPreset = this.#configService.getSync('infraPreset');
        return !((infraPreset !== 'b2e' && infraPreset !== 'b2eadmin') || this.#isCip);
    }

    /**
     * Ideally observability data should be sent directly to the MTC ACA endpoint.
     * This is to avoid observability of observability.
     * But CIP is another token which is not configured in the MTC ACA, so for CIP we need to go through the b2c gateway,
     * because it is anonymous.
     */
    getEndpointBaseUrl(): string | undefined {
        const stage = this.#configService.getSync('stage');
        if (this.#isCip) {
            switch (stage) {
                // DNS static defined domain: https://cwiki.mobicorp.ch/confluence/x/yJesBw
                case 'prod':
                    return 'https://sec-api-b2c.app.mobiliar.ch' + MTC_ACA_ENDPOINT;
                case 'preprod':
                    return 'https://sec-api-b2c.app.mobiliar-preprod.ch' + MTC_ACA_ENDPOINT;
                case 'sit':
                    return 'https://sec-api-b2c.app.mobiliar-sit.ch' + MTC_ACA_ENDPOINT;
                case 'local-dev':
                    return undefined;
                default:
                    break; // use fallback
            }
        }
        // B2E traffic is routed directly to the MTC ACA to optimize load on the gateway (APIM)
        const apiGateway = this.#configService.getSync('apiGatewayCloud');
        const infraPreset = this.#configService.getSync('infraPreset');
        if (infraPreset === 'b2e' || infraPreset === 'b2eadmin' || !apiGateway) {
            switch (stage) {
                // DNS static defined domain: https://cwiki.mobicorp.ch/confluence/x/yJesBw
                case 'prod':
                    return 'https://cos-telemetry-processor.app.mobiliar.ch' + MTC_ACA_ENDPOINT;
                case 'preprod':
                    return 'https://cos-telemetry-processor.app.mobiliar-preprod.ch' + MTC_ACA_ENDPOINT;
                case 'sit':
                    return 'https://cos-telemetry-processor.app.mobiliar-sit.ch' + MTC_ACA_ENDPOINT;
                case 'local-dev':
                    return undefined;
                default:
                    break; // use fallback
            }
        }
        if (!apiGateway) {
            console.error(
                'Could not find apiGatewayCloud in the configuration, no valid endpoint for observability could be use!'
            );
        }

        // Fallback: all other endpoints (b2c, azureDefault) go through the gateway
        return apiGateway + MTC_ACA_ENDPOINT;
    }
}

function generateTraceparent(): string {
    const version = '00';
    const traceId = crypto.randomUUID().replace(/-/g, '');
    const parentId = crypto.randomUUID().replace(/-/g, '').substring(0, 16);
    const traceFlags = '01';
    return `${version}-${traceId}-${parentId}-${traceFlags}`;
}
